import {useAuth} from "./../../../contexts/AuthContext"
import React, {Fragment, useState} from "react";
import {Page} from "../../layout/Pages";
import ListViewTable from "../../elements/tables/ListViewTable";
import {PrimaryButton} from "../../elements/Buttons";
import {toast} from "react-toastify";
import classNames from "classnames";
import {Dialog, Transition} from "@headlessui/react";
import {XMarkIcon} from "@heroicons/react/24/outline";

// Mock data for inbox items
const mockInboxData = [
    {
        id: 1,
        source: "Email",
        subject: "Need help with website redesign",
        summary: "Looking for a complete website overhaul with modern design",
        client: "Acme Corp",
        sender: {
            name: "John Smith",
            contact: "john.smith@acmecorp.com"
        },
        label: "Request",
        urgency: "Normal",
        content: "Hi there,\n\nWe're looking to completely redesign our website. Our current site is outdated and we need a modern, responsive design that works well on mobile devices. We'd like to discuss timeline and pricing.\n\nBest regards,\nJohn Smith\nCEO, Acme Corp",
        timestamp: "2024-01-26T10:30:00",
    },
    {
        id: 2,
        source: "Slack",
        subject: "Quick question about the logo",
        summary: "Client asking about logo file formats",
        client: "TechStart Inc",
        sender: {
            name: "Sarah Johnson",
            contact: "@sarah.johnson"
        },
        label: "Message",
        urgency: "Low",
        content: "Hey! Quick question - can you send me the logo in SVG format? I need it for our new business cards. Thanks!",
        timestamp: "2024-01-26T09:15:00",
    },
    {
        id: 3,
        source: "Asana",
        subject: "Project milestone completed",
        summary: "Phase 1 of the branding project is complete",
        client: "Brand Co",
        sender: {
            name: "Mike Davis",
            contact: "mike.davis@brandco.com"
        },
        label: "Non-request",
        urgency: "Low",
        content: "Task completed: Phase 1 - Brand Discovery\n\nAll deliverables have been uploaded to the shared folder. Ready for client review.",
        timestamp: "2024-01-26T08:45:00",
    },
    {
        id: 4,
        source: "Voice Note",
        subject: "Urgent: Website unresponsive",
        summary: "Client reporting partial website outage",
        client: "Retail Plus",
        sender: {
            name: "Sarah Martinez",
            contact: "+1 (555) 234-5678"
        },
        label: "Request",
        urgency: "High",
        content: "[Voice Note Transcript]\n\nHey, um, quick thing — since yesterday the website’s been acting weird.\n" +
            "The homepage loads but when I click any of the buttons it either takes forever or just doesn’t do anything.\n" +
            "We didn’t change anything on our side as far as I know, but I saw someone mention an update in Slack last week?\n" +
            "It’s not completely down but it’s definitely not usable for visitors.\n" +
            "Can someone please take a look today if possible?",
        timestamp: "2024-01-26T11:00:00",
    },
    {
        id: 5,
        source: "Email",
        subject: "Thank you for the great work!",
        summary: "Client expressing satisfaction with recent project",
        client: "Happy Client LLC",
        sender: {
            name: "Michelle Chen",
            contact: "michelle@happyclient.com"
        },
        label: "Non-request",
        urgency: "Low",
        content: "Hi Team,\n\nI just wanted to send a quick note to say thank you for the amazing work on our recent campaign. The results have exceeded our expectations and our stakeholders are thrilled!\n\nLooking forward to working together again soon.\n\nCheers,\nMichelle",
        timestamp: "2024-01-25T16:20:00",
    },
    {
        id: 6,
        source: "Slack",
        subject: "Can we schedule a call?",
        summary: "Request to schedule a strategy call",
        client: "Growth Startup",
        sender: {
            name: "David Park",
            contact: "@david.park"
        },
        label: "Request",
        urgency: "Normal",
        content: "Hi! Would love to schedule a call this week to discuss our Q2 marketing strategy. Do you have any availability on Thursday or Friday afternoon?",
        timestamp: "2024-01-26T07:30:00",
    },
    {
        id: 7,
        source: "Email",
        subject: "Invoice question",
        summary: "Question about recent invoice",
        client: "Acme Corp",
        sender: {
            name: "John Smith",
            contact: "john.smith@acmecorp.com"
        },
        label: "Message",
        urgency: "Normal",
        content: "Hello,\n\nI received the invoice for last month's work. Just wanted to clarify - does this include the additional revisions we discussed, or will those be billed separately?\n\nThanks,\nJohn",
        timestamp: "2024-01-25T14:10:00",
    },
    {
        id: 8,
        source: "Asana",
        subject: "New task assigned: Social media graphics",
        summary: "New design request for social media campaign",
        client: "TechStart Inc",
        sender: {
            name: "Sarah Johnson",
            contact: "sarah.j@techstart.com"
        },
        label: "Request",
        urgency: "High",
        content: "New Task: Create 10 social media graphics for upcoming product launch\n\nDue Date: February 5, 2024\nPriority: High\n\nDescription: Need eye-catching graphics for Instagram, LinkedIn, and Twitter to promote our new product launch.",
        timestamp: "2024-01-26T06:00:00",
    },
];

// Define columns as a function to get access to the click handler
const getColumns = (onRowClick) => [
    {
        accessorKey: "id",
        id: "select",
        columnType: "select",
        disableRowLink: true, // Disable row link for checkbox column
    },
    {
        accessorKey: "source",
        id: "source",
        header: "Source",
        enableColumnFilter: true,
        headerClassNames: "narrow-filter",
    },
    {
        accessorKey: "subject",
        id: "subject",
        header: "Subject / Summary",
        enableColumnFilter: false,
        cell: ({row}) => (
            <div className="cursor-pointer" onClick={() => onRowClick(row)}>
                <div className="font-medium text-gray-900">{row.original.subject}</div>
                <div className="text-sm text-gray-500">{row.original.summary}</div>
            </div>
        ),
        disableRowLink: true, // We handle click ourselves
        classNames: "min-w-96",
    },
    {
        accessorKey: "client",
        id: "client",
        header: "Client",
        enableColumnFilter: true,
        headerClassNames: "narrow-filter",
    },
    {
        accessorKey: "sender",
        id: "sender",
        header: "Sender",
        enableColumnFilter: false,
        cell: ({row}) => (
            <div>
                <div className="font-medium text-gray-900">{row.original.sender.name}</div>
                <div className="text-sm text-gray-500">{row.original.sender.contact}</div>
            </div>
        ),
    },
    {
        accessorKey: "label",
        id: "label",
        header: "Label",
        enableColumnFilter: true,
        headerClassNames: "narrow-filter",
        cell: ({row}) => {
            const value = row.original.label;
            const colorMap = {
                "Request": "bg-red-100 text-red-800",
                "Message": "bg-blue-100 text-blue-800",
                "Non-request": "bg-gray-100 text-gray-800",
            };
            return (
                <span className={classNames(
                    "inline-flex rounded-full px-2 text-xs font-semibold leading-5",
                    colorMap[value] || "bg-gray-100 text-gray-800"
                )}>
                    {value}
                </span>
            );
        }
    },
    {
        accessorKey: "urgency",
        id: "urgency",
        header: "Urgency",
        enableColumnFilter: true,
        headerClassNames: "narrow-filter",
        cell: ({row}) => {
            const value = row.original.urgency;
            const colorMap = {
                "High": "bg-red-100 text-red-800",
                "Normal": "bg-yellow-100 text-yellow-800",
                "Low": "bg-green-100 text-green-800",
            };
            return (
                <span className={classNames(
                    "inline-flex rounded-full px-2 text-xs font-semibold leading-5",
                    colorMap[value] || "bg-gray-100 text-gray-800"
                )}>
                    {value}
                </span>
            );
        }
    },
    {
        accessorKey: "timestamp",
        id: "timestamp",
        header: "Date",
        columnType: "date_time",
        enableColumnFilter: false,
    },
]

export default function InboxList() {

    const {currentUser} = useAuth()
    const [rowSelection, setRowSelection] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("needs-review");

    // Filter data based on active tab
    const getFilteredData = () => {
        if (activeTab === "needs-review") {
            // Show items that haven't been dismissed (in real app, this would be a status field)
            return mockInboxData.filter(item => item.id !== 3 && item.id !== 5); // Excluding some as "dismissed"
        } else if (activeTab === "dismissed") {
            // Show dismissed items
            return mockInboxData.filter(item => item.id === 3 || item.id === 5);
        } else {
            // Show all items
            return mockInboxData;
        }
    };

    const data = getFilteredData();
    const isLoading = false;
    const isSuccess = true;

    async function handleDelete() {
        toast.success('Items deleted.')
        setRowSelection([])
    }

    // This function is called when a row is clicked
    function handleRowClick(row) {
        setSelectedItem(row.original);
        setSidePanelOpen(true);
    }

    // Get columns with the click handler
    const columns = getColumns(handleRowClick);

    function handleProcessAsRequest() {
        if (rowSelection.length > 0) {
            toast.success(`Processing ${rowSelection.length} item(s) as request(s)`);
            setRowSelection([]);
        }
    }

    function handleDismiss() {
        if (rowSelection.length > 0) {
            toast.success(`Dismissed ${rowSelection.length} item(s)`);
            setRowSelection([]);
        }
    }

    function handleProcessAsRequestSidePanel() {
        toast.success('Processing item as request');
        setSidePanelOpen(false);
    }

    function handleDismissSidePanel() {
        toast.success('Item dismissed');
        setSidePanelOpen(false);
    }

    return (
        <Page>
            <div className="px-2">
                <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">Inbox</h1>
                    </div>
                </div>
                <div className="flex flex-col">
                    <div className="sm:-my-2 sm:-mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">

                        <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                            <nav className="flex space-x-4" aria-label="Tabs">
                                <a
                                    key="needs-review"
                                    onClick={() => setActiveTab("needs-review")}
                                    className={classNames(
                                        activeTab === "needs-review"
                                            ? "bg-indigo-100 text-indigo-700"
                                            : "text-gray-500 hover:text-gray-700",
                                        "px-3 py-2 font-medium text-sm rounded-md cursor-pointer"
                                    )}
                                    aria-current={activeTab === "needs-review" ? "page" : undefined}
                                >
                                    Needs Review
                                </a>
                                <a
                                    key="dismissed"
                                    onClick={() => setActiveTab("dismissed")}
                                    className={classNames(
                                        activeTab === "dismissed"
                                            ? "bg-indigo-100 text-indigo-700"
                                            : "text-gray-500 hover:text-gray-700",
                                        "px-3 py-2 font-medium text-sm rounded-md cursor-pointer"
                                    )}
                                    aria-current={activeTab === "dismissed" ? "page" : undefined}
                                >
                                    Dismissed
                                </a>
                                <a
                                    key="all"
                                    onClick={() => setActiveTab("all")}
                                    className={classNames(
                                        activeTab === "all"
                                            ? "bg-indigo-100 text-indigo-700"
                                            : "text-gray-500 hover:text-gray-700",
                                        "px-3 py-2 font-medium text-sm rounded-md cursor-pointer"
                                    )}
                                    aria-current={activeTab === "all" ? "page" : undefined}
                                >
                                    All
                                </a>
                            </nav>
                        </div>

                        <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                            {isSuccess && data && (
                                <ListViewTable
                                    data={data}
                                    columns={columns}
                                    isLoading={isLoading}
                                    disableGlobalSearch={false}
                                    rowSelection={rowSelection}
                                    setRowSelection={setRowSelection}
                                    customActions={[
                                        {
                                            label: 'Process as Request',
                                            onClick: handleProcessAsRequest,
                                            style: 'primary'
                                        },
                                        {
                                            label: 'Dismiss',
                                            onClick: handleDismiss,
                                            style: 'secondary'
                                        }
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Side Panel */}
            <Transition.Root show={sidePanelOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={setSidePanelOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-in-out duration-500"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-500"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                                        <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                                            <div className="bg-indigo-700 px-4 py-6 sm:px-6">
                                                <div className="flex items-center justify-between">
                                                    <Dialog.Title className="text-base font-semibold leading-6 text-white">
                                                        Inbox Item Details
                                                    </Dialog.Title>
                                                    <div className="ml-3 flex h-7 items-center">
                                                        <button
                                                            type="button"
                                                            className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                            onClick={() => setSidePanelOpen(false)}
                                                        >
                                                            <span className="absolute -inset-2.5" />
                                                            <span className="sr-only">Close panel</span>
                                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="relative flex-1 px-4 py-6 sm:px-6">
                                                {selectedItem && (
                                                    <div className="space-y-6">
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Source</h3>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedItem.source}</p>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Subject</h3>
                                                            <p className="mt-1 text-base font-semibold text-gray-900">{selectedItem.subject}</p>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Summary</h3>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedItem.summary}</p>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Client</h3>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedItem.client}</p>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Label</h3>
                                                            <span className={classNames(
                                                                "mt-1 inline-flex rounded-full px-2 text-xs font-semibold leading-5",
                                                                selectedItem.label === "Request" ? "bg-red-100 text-red-800" :
                                                                selectedItem.label === "Message" ? "bg-blue-100 text-blue-800" :
                                                                "bg-gray-100 text-gray-800"
                                                            )}>
                                                                {selectedItem.label}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Full Content</h3>
                                                            <div className="mt-2 rounded-md bg-gray-50 p-4">
                                                                <p className="whitespace-pre-wrap text-sm text-gray-900">{selectedItem.content}</p>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-medium text-gray-500">Timestamp</h3>
                                                            <p className="mt-1 text-sm text-gray-900">
                                                                {new Date(selectedItem.timestamp).toLocaleString()}
                                                            </p>
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div className="flex space-x-3 pt-6 border-t border-gray-200">
                                                            <PrimaryButton onClick={handleProcessAsRequestSidePanel} classNameIn="flex-1" label="Process as Request" />
                                                            <button
                                                                type="button"
                                                                onClick={handleDismissSidePanel}
                                                                className="flex-1 inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                            >
                                                                Dismiss
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </Page>
    )
}

